<template>
  <div dir="auto">
    <div
      class="flex justify-between items-center w-full"
      :class="{ 'mb-2': !field.description }"
    >
      <label
        :for="field.uuid"
        class="label text-2xl"
      >
        <MarkdownContent
          v-if="field.title"
          :string="field.title"
        />
        <template v-else>
          {{ field.name && showFieldNames ? field.name : t('date') }}
          <template v-if="!field.required">
            ({{ t('optional') }})
          </template>
        </template>
      </label>
      <button
        v-if="!isSenderPrefilled"
        class="btn btn-outline btn-sm !normal-case font-normal"
        @click.prevent="[setCurrentDate(), $emit('focus')]"
      >
        <IconCalendarCheck :width="16" />
        {{ t('set_today') }}
      </button>
    </div>
    <div
      v-if="field.description && !isSenderPrefilled"
      class="mb-3 px-1"
      dir="auto"
    >
      <MarkdownContent :string="field.description" />
    </div>
    <AppearsOn :field="field" />
    <!-- Sender pre-filled: read-only display -->
    <div v-if="isSenderPrefilled" class="text-center">
      <div class="text-2xl py-3 px-4 border rounded-lg bg-base-200">
        {{ formattedPrefillDate }}
      </div>
      <input
        type="hidden"
        :name="`values[${field.uuid}]`"
        :value="value"
      >
    </div>
    <!-- Normal: recipient picks date -->
    <div v-else class="text-center">
      <input
        ref="input"
        v-model="value"
        class="base-input !text-2xl text-center w-full"
        :required="field.required"
        type="date"
        :name="`values[${field.uuid}]`"
        @focus="$emit('focus')"
      >
      <p class="text-xs text-base-content/50 mt-1">
        Ensure the selected date aligns with the field label (e.g. Start Date, Effective Date).
      </p>
    </div>
  </div>
</template>

<script>
import { IconCalendarCheck } from '@tabler/icons-vue'
import AppearsOn from './appears_on'
import MarkdownContent from './markdown_content'

export default {
  name: 'DateStep',
  components: {
    IconCalendarCheck,
    MarkdownContent,
    AppearsOn
  },
  inject: ['t'],
  props: {
    field: {
      type: Object,
      required: true
    },
    showFieldNames: {
      type: Boolean,
      required: false,
      default: true
    },
    modelValue: {
      type: String,
      required: false,
      default: ''
    }
  },
  emits: ['update:model-value', 'focus'],
  computed: {
    isSenderPrefilled () {
      return !!this.field.description && this.field.description.match(/^\d{4}-\d{2}-\d{2}$/)
    },
    formattedPrefillDate () {
      if (!this.isSenderPrefilled) return ''

      try {
        const date = new Date(this.field.description + 'T00:00:00')
        const format = this.field.preferences?.format || 'MMMM D, YYYY'

        return this.formatDateWithFormat(date, format)
      } catch {
        return this.field.description
      }
    },
    value: {
      set (value) {
        this.$emit('update:model-value', value)
      },
      get () {
        if (this.isSenderPrefilled && !this.modelValue) {
          return this.field.description
        }
        return this.modelValue
      }
    }
  },
  mounted () {
    if (this.isSenderPrefilled && !this.modelValue) {
      this.$emit('update:model-value', this.field.description)
    }
  },
  methods: {
    formatDateWithFormat (date, format) {
      const monthFormats = {
        M: 'numeric',
        MM: '2-digit',
        MMM: 'short',
        MMMM: 'long'
      }

      const dayFormats = {
        D: 'numeric',
        DD: '2-digit'
      }

      const yearFormats = {
        YYYY: 'numeric',
        YY: '2-digit'
      }

      const monthMatch = format.match(/M+/)
      const dayMatch = format.match(/D+/)
      const yearMatch = format.match(/Y+/)

      const parts = new Intl.DateTimeFormat([], {
        day: dayFormats[dayMatch?.[0]] || 'numeric',
        month: monthFormats[monthMatch?.[0]] || 'long',
        year: yearFormats[yearMatch?.[0]] || 'numeric'
      }).formatToParts(date)

      return format
        .replace(/D+/, parts.find((p) => p.type === 'day')?.value || '')
        .replace(/M+/, parts.find((p) => p.type === 'month')?.value || '')
        .replace(/Y+/, parts.find((p) => p.type === 'year')?.value || '')
    },
    setCurrentDate () {
      const inputEl = this.$refs.input

      inputEl.valueAsDate = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000)

      inputEl.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }
}
</script>
